import React, { useState, useEffect, useMemo } from 'react';
import { handleNumericInput, handleNumericKeyDown, processNumericInput } from '../utils/inputUtils';
import { ArrowLeftRight, Package, Calendar, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { NativeSelect } from './ui/native-select';
import axios from 'axios';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const TransferenciasTab = () => {
  const [stocks, setStocks] = useState([]);
  const [transferencias, setTransferencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dataFiltro, setDataFiltro] = useState(new Date().toISOString().split('T')[0]);
  const [showModal, setShowModal] = useState(false);
  
  const [formData, setFormData] = useState({
    data: new Date().toISOString().split('T')[0],
    turno_origem: 'Manhã',
    turno_destino: 'Tarde',
    raspadinha_1: 0,
    raspadinha_2: 0,
    raspadinha_3: 0,
    raspadinha_5: 0,
    raspadinha_10: 0,
    raspadinha_15: 0,
    observacoes: ''
  });

  useEffect(() => {
    loadData();
  }, [dataFiltro]);

  // Recarregar stocks quando o modal abrir
  useEffect(() => {
    if (showModal) {
      loadStocksDisponiveis();
    }
  }, [showModal]);

  const loadStocksDisponiveis = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('🔍 Buscando stocks disponíveis (últimos registrados)');
      // Buscar sem filtro de data - pega os stocks mais recentes
      const response = await axios.get(`${API_URL}/api/stocks/disponiveis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('📦 Stocks recebidos:', response.data);
      setStocks(response.data);
    } catch (err) {
      console.error('Erro ao carregar stocks:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Buscar stocks disponíveis (sem filtro de data - sempre os mais recentes)
      // e transferências filtradas por data
      const [stocksRes, transRes] = await Promise.all([
        axios.get(`${API_URL}/api/stocks/disponiveis`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/transferencias`, {
          params: { data: dataFiltro },
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStocks(stocksRes.data);
      setTransferencias(transRes.data);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar dados');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que pelo menos uma raspadinha foi selecionada
    const total = formData.raspadinha_1 + formData.raspadinha_2 + formData.raspadinha_3 + 
                  formData.raspadinha_5 + formData.raspadinha_10 + formData.raspadinha_15;
    
    if (total === 0) {
      setError('Selecione pelo menos uma raspadinha para transferir');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Adicionar data atual automaticamente
      const dataTransferencia = {
        ...formData,
        data: new Date().toISOString().split('T')[0] // Data de hoje
      };
      
      await axios.post(
        `${API_URL}/api/transferencias`,
        dataTransferencia,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess('Transferência realizada com sucesso!');
      setShowModal(false);
      resetForm();
      loadData();
      
      // Disparar evento para notificar outros componentes (ex: StocksTab)
      window.dispatchEvent(new CustomEvent('stockModificado'));
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao realizar transferência');
    }
  };

  const resetForm = () => {
    setFormData({
      data: dataFiltro,
      turno_origem: 'Manhã',
      turno_destino: 'Tarde',
      raspadinha_1: 0,
      raspadinha_2: 0,
      raspadinha_3: 0,
      raspadinha_5: 0,
      raspadinha_10: 0,
      raspadinha_15: 0,
      observacoes: ''
    });
  };

  const getStockPorTurno = (turno) => {
    console.log('🔎 Procurando stock para turno:', turno);
    console.log('📊 Stocks disponíveis:', stocks);
    const stock = stocks.find(s => s.turno === turno);
    console.log('✅ Stock encontrado:', stock);
    return stock;
  };

  const inverteTurno = () => {
    setFormData({
      ...formData,
      turno_origem: formData.turno_destino,
      turno_destino: formData.turno_origem
    });
  };

  // useMemo DEVE estar antes de qualquer return condicional
  const stockOrigem = useMemo(() => {
    console.log('🔄 Recalculando stockOrigem para turno:', formData.turno_origem);
    console.log('📊 Stocks disponíveis:', stocks);
    const stock = stocks.find(s => s.turno === formData.turno_origem);
    console.log('✅ Stock encontrado:', stock);
    return stock;
  }, [formData.turno_origem, stocks]);

  if (loading) return <div className="text-center py-8">Carregando...</div>;

  return (
    <div className="space-y-6">
      {/* Header com filtro de data */}
      <Card className="bg-gradient-card border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Calendar className="h-5 w-5 text-primary" />
              <span className="font-semibold">Data:</span>
              <input
                type="date"
                value={dataFiltro}
                onChange={(e) => setDataFiltro(e.target.value)}
                className="px-3 py-2 border rounded-md bg-background"
              />
            </div>
            
            <Button 
              onClick={() => { setFormData({...formData, data: dataFiltro}); setShowModal(true); }} 
              className="bg-primary hover:bg-primary/90"
            >
              <ArrowLeftRight className="h-4 w-4 mr-2" />
              Nova Transferência
            </Button>
          </div>
        </CardContent>
      </Card>

      {success && (
        <div className="bg-success/10 border border-success text-success px-4 py-3 rounded relative flex items-center gap-2">
          <CheckCircle className="h-4 w-4" />
          <span>{success}</span>
        </div>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded relative flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Modal de Transferência */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto relative z-50">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <ArrowLeftRight className="h-5 w-5" />
                Nova Transferência
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                <div>
                  <label className="block text-sm font-medium mb-2">Turno Origem</label>
                  <NativeSelect
                    value={formData.turno_origem}
                    onChange={(e) => {
                      const novoOrigem = e.target.value;
                      const novoDestino = novoOrigem === 'Manhã' ? 'Tarde' : 'Manhã';
                      setFormData({
                        ...formData, 
                        turno_origem: novoOrigem,
                        turno_destino: novoDestino
                      });
                    }}
                    className="w-full"
                    required
                  >
                    <option value="Manhã">Manhã</option>
                    <option value="Tarde">Tarde</option>
                  </NativeSelect>
                </div>

                <div className="flex items-end">
                  <Button 
                    type="button"
                    onClick={inverteTurno}
                    className="w-full"
                    variant="outline"
                  >
                    <ArrowLeftRight className="h-4 w-4 mr-2" />
                    Inverter
                  </Button>
                </div>
              </div>

              {/* Indicação visual da transferência */}
              <div className="flex items-center justify-center gap-2 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                <span className="font-bold text-blue-700">{formData.turno_origem}</span>
                <ArrowLeftRight className="h-5 w-5 text-blue-600" />
                <span className="font-bold text-blue-700">{formData.turno_destino}</span>
              </div>

              {/* Stock disponível */}
              {stockOrigem && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm font-semibold mb-2">Stock Disponível ({formData.turno_origem}):</p>
                  <div className="grid grid-cols-6 gap-2 text-sm">
                    <div><span className="font-medium">1€:</span> {stockOrigem.raspadinha_1}</div>
                    <div><span className="font-medium">2€:</span> {stockOrigem.raspadinha_2}</div>
                    <div><span className="font-medium">3€:</span> {stockOrigem.raspadinha_3}</div>
                    <div><span className="font-medium">5€:</span> {stockOrigem.raspadinha_5}</div>
                    <div><span className="font-medium">10€:</span> {stockOrigem.raspadinha_10}</div>
                    <div><span className="font-medium">15€:</span> {stockOrigem.raspadinha_15}</div>
                  </div>
                </div>
              )}

              {!stockOrigem && (
                <div className="p-4 bg-destructive/10 border border-destructive rounded-lg text-destructive">
                  <AlertCircle className="h-4 w-4 inline mr-2" />
                  Não há stock disponível para {formData.turno_origem} nesta data
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Raspadinhas 1€</label>
                  <input
                    type="text" inputMode="decimal"
                    min="0"
                    max={stockOrigem?.raspadinha_1 || 0}
                    value={formData.raspadinha_1}
                    onChange={(e) => setFormData({...formData, raspadinha_1: parseInt(e.target.value.replace(',', '.')) || 0})}
                    onKeyDown={handleNumericKeyDown}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    disabled={!stockOrigem}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Raspadinhas 2€</label>
                  <input
                    type="text" inputMode="decimal"
                    min="0"
                    max={stockOrigem?.raspadinha_2 || 0}
                    value={formData.raspadinha_2}
                    onChange={(e) => setFormData({...formData, raspadinha_2: parseInt(e.target.value.replace(',', '.')) || 0})}
                    onKeyDown={handleNumericKeyDown}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    disabled={!stockOrigem}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Raspadinhas 3€</label>
                  <input
                    type="text" inputMode="decimal"
                    min="0"
                    max={stockOrigem?.raspadinha_3 || 0}
                    value={formData.raspadinha_3}
                    onChange={(e) => setFormData({...formData, raspadinha_3: parseInt(e.target.value.replace(',', '.')) || 0})}
                    onKeyDown={handleNumericKeyDown}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    disabled={!stockOrigem}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Raspadinhas 5€</label>
                  <input
                    type="text" inputMode="decimal"
                    min="0"
                    max={stockOrigem?.raspadinha_5 || 0}
                    value={formData.raspadinha_5}
                    onChange={(e) => setFormData({...formData, raspadinha_5: parseInt(e.target.value.replace(',', '.')) || 0})}
                    onKeyDown={handleNumericKeyDown}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    disabled={!stockOrigem}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Raspadinhas 10€</label>
                  <input
                    type="text" inputMode="decimal"
                    min="0"
                    max={stockOrigem?.raspadinha_10 || 0}
                    value={formData.raspadinha_10}
                    onChange={(e) => setFormData({...formData, raspadinha_10: parseInt(e.target.value.replace(',', '.')) || 0})}
                    onKeyDown={handleNumericKeyDown}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    disabled={!stockOrigem}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Raspadinhas 15€</label>
                  <input
                    type="text" inputMode="decimal"
                    min="0"
                    max={stockOrigem?.raspadinha_15 || 0}
                    value={formData.raspadinha_15}
                    onChange={(e) => setFormData({...formData, raspadinha_15: parseInt(e.target.value.replace(',', '.')) || 0})}
                    onKeyDown={handleNumericKeyDown}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    disabled={!stockOrigem}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Observações</label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowModal(false); resetForm(); }}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-primary hover:bg-primary/90"
                  disabled={!stockOrigem}
                >
                  Transferir
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stocks Atuais */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {['Manhã', 'Tarde'].map(turno => {
          const stock = getStockPorTurno(turno);
          return (
            <Card key={turno}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Stock {turno}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stock ? (
                  <>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-3 bg-gradient-card rounded-lg border border-border">
                        <p className="text-xs text-muted-foreground mb-1">1€</p>
                        <p className="text-2xl font-bold text-primary">{stock.raspadinha_1}</p>
                      </div>
                      <div className="p-3 bg-gradient-card rounded-lg border border-border">
                        <p className="text-xs text-muted-foreground mb-1">2€</p>
                        <p className="text-2xl font-bold text-primary">{stock.raspadinha_2}</p>
                      </div>
                      <div className="p-3 bg-gradient-card rounded-lg border border-border">
                        <p className="text-xs text-muted-foreground mb-1">3€</p>
                        <p className="text-2xl font-bold text-primary">{stock.raspadinha_3}</p>
                      </div>
                      <div className="p-3 bg-gradient-card rounded-lg border border-border">
                        <p className="text-xs text-muted-foreground mb-1">5€</p>
                        <p className="text-2xl font-bold text-primary">{stock.raspadinha_5}</p>
                      </div>
                      <div className="p-3 bg-gradient-card rounded-lg border border-border">
                        <p className="text-xs text-muted-foreground mb-1">10€</p>
                        <p className="text-2xl font-bold text-primary">{stock.raspadinha_10}</p>
                      </div>
                      <div className="p-3 bg-gradient-card rounded-lg border border-border">
                        <p className="text-xs text-muted-foreground mb-1">15€</p>
                        <p className="text-2xl font-bold text-primary">{stock.raspadinha_15}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-border text-center">
                      <p className="text-sm text-muted-foreground">Total</p>
                      <p className="text-xl font-bold">
                        {stock.raspadinha_1 + stock.raspadinha_2 + stock.raspadinha_3 + 
                         stock.raspadinha_5 + stock.raspadinha_10 + stock.raspadinha_15} raspadinhas
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Sem stock registrado</p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Histórico de Transferências */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Transferências</CardTitle>
        </CardHeader>
        <CardContent>
          {transferencias.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ArrowLeftRight className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma transferência realizada nesta data</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transferencias.map(trans => (
                <div key={trans.id} className="p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <ArrowLeftRight className="h-4 w-4 text-primary" />
                      <span className="font-semibold">{trans.turno_origem} → {trans.turno_destino}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {new Date(trans.created_at).toLocaleString('pt-PT')}
                    </span>
                  </div>
                  <div className="grid grid-cols-6 gap-2 text-sm">
                    {trans.raspadinha_1 > 0 && <div>1€: {trans.raspadinha_1}</div>}
                    {trans.raspadinha_2 > 0 && <div>2€: {trans.raspadinha_2}</div>}
                    {trans.raspadinha_3 > 0 && <div>3€: {trans.raspadinha_3}</div>}
                    {trans.raspadinha_5 > 0 && <div>5€: {trans.raspadinha_5}</div>}
                    {trans.raspadinha_10 > 0 && <div>10€: {trans.raspadinha_10}</div>}
                    {trans.raspadinha_15 > 0 && <div>15€: {trans.raspadinha_15}</div>}
                  </div>
                  {trans.observacoes && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Obs: {trans.observacoes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TransferenciasTab;
